import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartErrorEvent } from '@shopify/events';

/**
 * How long to wait after a keystroke before writing to the cart. Matches
 * 'cart-note.js'. Every millisecond here is a window in which an accelerated
 * checkout button can carry the shopper away before the write lands, so it is
 * deliberately short and deliberately not the only path — see '#commit'.
 */
const SAVE_DELAY = 200;

/**
 * The company-invoice fields in the cart (see 'snippets/davitta-cart-invoice.liquid').
 *
 * Two jobs: persist the three fields as cart attributes, and tell the shopper when
 * the CUI cannot be a real one.
 *
 * **Persisting is the part that has to be right**, because the value ends up on a
 * legal document. There are three writers, in order of how much of the window they
 * cover:
 *
 * 1. '#commit' on 'change' — fires the moment the field is left, undebounced. This
 *    is what covers the accelerated checkout buttons (Shop Pay, PayPal, Apple Pay),
 *    which sit beside the Checkout button but do **not** submit 'cart-form', so the
 *    markup's 'form=' fallback does nothing for them. A mousedown on one of those
 *    buttons blurs the field, and 'change' fires before the click.
 * 2. 'save' on 'input' — debounced, so a shopper who types and then navigates
 *    without leaving the field still has the value written.
 * 3. '#flush' on 'pagehide' — 'keepalive' so the request outlives the page, for the
 *    case where neither of the above got there first.
 *
 * On top of that, the inputs are bound to 'cart-form' in the markup, so the plain
 * Checkout button re-POSTs them with the form. Belt and braces, because the failure
 * is silent and lands on an invoice.
 *
 * An implausible CUI is still saved. Losing the shopper's stated intent is worse than
 * passing a bad CUI downstream, where an ANAF lookup rejects it and flags the order.
 *
 * @typedef {Object} CartInvoiceRefs
 * @property {HTMLInputElement} [company] - Company name
 * @property {HTMLInputElement} [cui] - CUI / VAT identification number
 * @property {HTMLInputElement} [regCom] - Trade registry number
 * @property {HTMLElement} [cuiError] - Server-rendered CUI message
 * @property {HTMLElement} [pairError] - Server-rendered "both fields" message
 *
 * @extends {Component<CartInvoiceRefs>}
 */
class DavittaCartInvoice extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {AbortController | undefined} */
  #listeners;

  /**
   * The last payload written, so leaving a field the shopper never touched does not
   * post the cart again, and 'pagehide' stays quiet for the B2C majority.
   * @type {string | null}
   */
  #lastSent = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    // Delegated from the component root rather than bound to the inputs: morph can
    // replace markup underneath us, and a listener on the element would go with it.
    // 'focusout' rather than 'blur' because only the bubbling one can be delegated.
    this.addEventListener('focusout', this.#validateCui, { signal });
    this.addEventListener('change', this.#commit, { signal });
    this.addEventListener('keydown', this.#blockImplicitSubmit, { signal });

    window.addEventListener('pagehide', this.#flush, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    // The pending timer is deliberately *not* cancelled: if it fires after a cart
    // re-render it writes what the shopper typed, which is the outcome we want.
  }

  /**
   * Handles typing. Bound declaratively in the markup ('on:input="/save"') rather
   * than here, because the theme's delegated listener runs 'customElements.upgrade'
   * on the way in — so keystrokes that land before this module executes still reach
   * us, which a listener added in 'connectedCallback' could never see.
   *
   * @param {Event} event - The input event from one of the fields.
   */
  save = (event) => {
    // Clearing the warning is not debounced. It used to be, and that made it useless:
    // the reveal happens on blur, ~100ms after the last keystroke, and the debounced
    // body then wiped it 200ms later — so a shopper who tabbed away quickly saw the
    // message flash and vanish, and typing in either *other* field cleared it for
    // good. Recovery feedback must not be rate-limited by a cart POST.
    if (event.target === this.refs.cui) this.#showCuiError(false);
    if (this.refs.pairError) this.refs.pairError.hidden = true;

    this.#queueSave(event);
  };

  #queueSave = debounce((/** @type {Event} */ event) => {
    void this.#persist({ event });
  }, SAVE_DELAY);

  /**
   * Normalizes the field that was just left and writes immediately.
   *
   * Normalizing here rather than on the way out is what keeps the two write paths
   * telling the same story: the browser does not trim a text input on submit, so the
   * 'cart-form' POST would otherwise overwrite a trimmed AJAX value with the padded
   * one the shopper pasted.
   *
   * @param {Event} event
   */
  #commit = (event) => {
    const { company, cui, regCom } = this.refs;
    const target = event.target;

    if (target !== company && target !== cui && target !== regCom) return;

    if (target === cui && cui) cui.value = normalizeCui(cui.value);
    else {
      const input = /** @type {HTMLInputElement} */ (target);
      input.value = input.value.trim();
    }

    if (target !== regCom) this.#validate();

    this.#queueSave.cancel?.();
    void this.#persist({ event });
  };

  /**
   * Enter in a text input triggers implicit submission, and the only submit button
   * 'cart-form' owns is Checkout — so Enter here would send the shopper straight into
   * checkout with a half-typed CUI. The seller note escapes this by being a
   * textarea, and the discount field by living in its own throwaway form.
   *
   * @param {KeyboardEvent} event
   */
  #blockImplicitSubmit = (event) => {
    if (event.key !== 'Enter') return;

    const { company, cui, regCom } = this.refs;
    if (![company, cui, regCom].includes(/** @type {any} */ (event.target))) return;

    event.preventDefault();
    if (event.target === cui) this.#validate();
  };

  /**
   * Both messages are re-taken when either half of the pair is left, not just the
   * CUI: "you filled one and not the other" is only knowable once the shopper has
   * moved on from whichever one they did fill.
   *
   * @param {FocusEvent} event
   */
  #validateCui = (event) => {
    const { company, cui } = this.refs;
    if (event.target !== cui && event.target !== company) return;

    this.#validate();
  };

  /** Re-takes both verdicts and shows or hides the two messages. */
  #validate() {
    const { company, cui, pairError } = this.refs;
    if (!cui) return;

    this.#showCuiError(isSuspectCui(normalizeCui(cui.value)));

    // One of the pair filled and the other not. Only ever shown after a field has
    // been left, never while the shopper is still working through them.
    if (pairError && company) {
      pairError.hidden = Boolean(company.value.trim()) === Boolean(cui.value.trim());
    }
  }

  /** @param {boolean} suspect */
  #showCuiError(suspect) {
    const { cui, cuiError } = this.refs;

    if (cuiError) cuiError.hidden = !suspect;

    if (suspect) cui?.setAttribute('aria-invalid', 'true');
    else cui?.removeAttribute('aria-invalid');
  }

  /** Last chance to write, on the way out of the page. */
  #flush = () => {
    this.#queueSave.cancel?.();
    void this.#persist({ keepalive: true });
  };

  /** The three attributes as they should reach the order. */
  #attributes() {
    const { company, cui, regCom } = this.refs;

    // An empty string removes the attribute rather than storing a blank one, which
    // is what clearing a field should mean to whatever reads the order.
    return {
      invoice_company: company?.value.trim() ?? '',
      invoice_cui: normalizeCui(cui?.value ?? ''),
      invoice_reg_com: regCom?.value.trim() ?? '',
    };
  }

  /**
   * Writes the attributes to the cart.
   *
   * @param {{event?: Event, keepalive?: boolean}} [options]
   */
  async #persist({ event, keepalive = false } = {}) {
    const attributes = this.#attributes();
    const payload = JSON.stringify({ attributes });

    if (payload === this.#lastSent) return;

    // A 'keepalive' request cannot be aborted usefully — the point of it is to
    // outlive this document — so it neither takes nor clears the slot.
    const controller = keepalive ? null : new AbortController();

    if (controller) {
      this.#activeFetch?.abort();
      this.#activeFetch = controller;
    }

    this.#lastSent = payload;

    try {
      const config = fetchConfig('json', { body: payload });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        ...(controller ? { signal: controller.signal } : {}),
        keepalive,
      });

      if (!response.ok) throw new Error(`Cart update responded ${response.status}`);
    } catch (error) {
      // Aborts are this component superseding its own in-flight request, not a
      // failure — and the request that superseded it owns the payload now, so the
      // bookkeeping must not be rolled back.
      if (error instanceof Error && error.name === 'AbortError') return;

      // Anything else and the cart does not hold what we just claimed it holds, so
      // the next edit has to try again rather than being skipped as a no-op.
      this.#lastSent = null;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to update invoice details',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      // Only if it is still ours. Aborting a request resumes its catch *after* the
      // request that replaced it has already taken the slot, so an unconditional
      // clear here would leave the newer request unabortable — and two writes racing
      // over the same cart can land in either order, which on a fiscal code means an
      // invoice against a half-typed CUI.
      if (this.#activeFetch === controller) this.#activeFetch = null;

      if (event) cartPerformance.measureFromEvent('invoice-details-update:user-action', event);
    }
  }
}

/**
 * Squeezes a CUI into the one form everything downstream should see: no spaces, no
 * dots, upper case.
 *
 * The 'RO' prefix is preserved rather than stripped — it is the signal that the
 * company is registered for VAT, which is not the same thing as having a CUI, and
 * an invoice must not treat one as the other.
 *
 * @param {string} value
 * @returns {string}
 */
function normalizeCui(value) {
  return value.replace(/[\s.]+/g, '').toUpperCase();
}

/**
 * Whether a CUI looks like a typo rather than a real identifier.
 *
 * Only Romanian CUIs are judged. A 13-digit CNP (which Oblio accepts as a client
 * 'cif' for a self-employed buyer) and a foreign EU VAT number both fail a Romanian
 * control-digit test by construction, and nagging a customer who typed their own
 * correct identifier is worse than staying quiet — the check exists to catch
 * slips, not to police the field.
 *
 * @param {string} normalized - Output of 'normalizeCui'.
 * @returns {boolean}
 */
function isSuspectCui(normalized) {
  if (!/^(RO)?\d{2,10}$/.test(normalized)) return false;

  return !hasValidCheckDigit(normalized.replace(/^RO/, ''));
}

/**
 * The Romanian CUI control digit: the preceding digits are right-aligned against
 * the key below, the weighted sum is multiplied by 10 and taken modulo 11, and 10
 * counts as 0. This catches the mistakes people actually make — a dropped or
 * transposed digit — which no length or character rule can see.
 *
 * It says nothing about whether the company exists. That is ANAF's answer, and it is
 * asked for on the way to Oblio, not here.
 *
 * @param {string} digits - Digits only, 2-10 of them.
 * @returns {boolean}
 */
function hasValidCheckDigit(digits) {
  const key = [7, 5, 3, 2, 1, 7, 5, 3, 2];
  const body = digits.slice(0, -1).padStart(key.length, '0');

  let sum = 0;
  for (let index = 0; index < key.length; index++) {
    sum += Number(body[index]) * Number(key[index]);
  }

  return ((sum * 10) % 11) % 10 === Number(digits.at(-1));
}

// The '-component' suffix is what makes the element eligible for the theme's
// declarative bindings and its upgrade-race retry — 'on:input="/save"' in the
// snippet resolves through it.
if (!customElements.get('davitta-cart-invoice-component')) {
  customElements.define('davitta-cart-invoice-component', DavittaCartInvoice);
}
